import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts';
import { TrendingUp, BarChart3, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fallback palette for materials without a configured color
const PALETTE = ['#f59e0b', '#1e293b', '#10b981', '#3b82f6', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6'];

const PERIOD_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

const RANGE_OPTIONS = [
  { value: 14, label: '14 days' },
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
  { value: 180, label: '6 months' },
];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((sum, p) => sum + (p.value || 0), 0);
  return (
    <div className="bg-white border rounded-lg shadow-lg p-3 min-w-[160px]">
      <p className="text-xs font-semibold text-slate-700 mb-2">{label}</p>
      {payload.map(p => (
        <div key={p.dataKey} className="flex items-center justify-between gap-4 text-xs mb-0.5">
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full flex-shrink-0" style={{ backgroundColor: p.fill }} />
            {p.dataKey}
          </span>
          <span className="font-medium">{p.value.toFixed(1)} m³</span>
        </div>
      ))}
      {payload.length > 1 && (
        <div className="mt-1.5 pt-1.5 border-t text-xs flex justify-between font-semibold">
          <span>Total</span><span>{total.toFixed(1)} m³</span>
        </div>
      )}
    </div>
  );
};

const PieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
  if (percent < 0.05) return null;
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight="600">
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

const ProductionAnalyticsChart = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('daily');
  const [days, setDays] = useState(30);
  const [chartType, setChartType] = useState('bar'); // 'bar' | 'pie'

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const endDate = new Date();
      const startDate = new Date(endDate);
      startDate.setDate(startDate.getDate() - days);
      const fmt = (d) => d.toISOString().slice(0, 10);
      const res = await axios.get(
        `${API}/production/analytics?period=${period}&start_date=${fmt(startDate)}&end_date=${fmt(endDate)}`,
        { headers: authHeaders() }
      );
      setData(res.data);
    } catch {
      // Non-critical
    } finally {
      setLoading(false);
    }
  }, [period, days]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  const getColor = (matName, idx) =>
    data?.mat_colors?.[matName] || PALETTE[idx % PALETTE.length];

  const formatXAxis = (val) => {
    if (period === 'monthly') {
      try { return new Date(val + '-01').toLocaleString('default', { month: 'short', year: '2-digit' }); }
      catch { return val; }
    }
    if (period === 'daily') {
      try { return new Date(val).toLocaleDateString('default', { month: 'short', day: 'numeric' }); }
      catch { return val; }
    }
    return val;
  };

  return (
    <div className="bg-white rounded-xl border shadow-sm overflow-hidden" data-testid="production-analytics">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b bg-slate-50">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-slate-700" />
          <h3 className="font-semibold text-slate-900">Excavation Analytics</h3>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Chart type toggle */}
          <div className="flex rounded-lg border overflow-hidden text-xs">
            <button
              className={`px-3 py-1.5 font-medium ${chartType === 'bar' ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
              onClick={() => setChartType('bar')}
            >Volume Over Time</button>
            <button
              className={`px-3 py-1.5 font-medium ${chartType === 'pie' ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
              onClick={() => setChartType('pie')}
            >Material Mix</button>
          </div>
          {/* Period */}
          <div className="flex rounded-lg border overflow-hidden text-xs">
            {PERIOD_OPTIONS.map(p => (
              <button key={p.value}
                className={`px-3 py-1.5 font-medium ${period === p.value ? 'bg-slate-700 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                onClick={() => setPeriod(p.value)}>
                {p.label}
              </button>
            ))}
          </div>
          {/* Range */}
          <select
            value={days}
            onChange={e => setDays(Number(e.target.value))}
            className="border rounded-lg px-2 py-1.5 text-xs text-slate-700 bg-white"
          >
            {RANGE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </div>
      </div>

      {/* Summary strip */}
      {data && !loading && (
        <div className="grid grid-cols-3 gap-0 divide-x border-b">
          <div className="px-5 py-3 text-center">
            <p className="text-xs text-slate-500">Total Volume</p>
            <p className="text-xl font-bold text-slate-900">{data.total_volume.toLocaleString()} m³</p>
          </div>
          <div className="px-5 py-3 text-center">
            <p className="text-xs text-slate-500">Total Trips</p>
            <p className="text-xl font-bold text-slate-900">{data.total_trips.toLocaleString()}</p>
          </div>
          <div className="px-5 py-3 text-center">
            <p className="text-xs text-slate-500">Top Material</p>
            <p className="text-xl font-bold text-slate-900">
              {data.mat_totals[0]?.name || '—'}
            </p>
          </div>
        </div>
      )}

      {/* Chart area */}
      <div className="p-5">
        {loading ? (
          <div className="h-64 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
          </div>
        ) : !data || data.chart_data.length === 0 ? (
          <div className="h-64 flex flex-col items-center justify-center text-slate-400">
            <TrendingUp className="h-12 w-12 mb-2 opacity-30" />
            <p className="text-sm">No production data for this period</p>
            <p className="text-xs mt-1">Add trips to see analytics</p>
          </div>
        ) : chartType === 'bar' ? (
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={data.chart_data} margin={{ top: 4, right: 16, left: 0, bottom: 24 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis
                dataKey="date"
                tickFormatter={formatXAxis}
                tick={{ fontSize: 11, fill: '#64748b' }}
                angle={-30}
                textAnchor="end"
                interval="preserveStartEnd"
              />
              <YAxis
                tick={{ fontSize: 11, fill: '#64748b' }}
                tickFormatter={v => `${v}m³`}
                width={54}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
                formatter={(value) => <span style={{ color: '#475569' }}>{value}</span>}
              />
              {data.materials.map((mat, idx) => (
                <Bar
                  key={mat}
                  dataKey={mat}
                  stackId="vol"
                  fill={getColor(mat, idx)}
                  radius={idx === data.materials.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        ) : (
          /* Pie chart — material mix */
          <div className="flex flex-col md:flex-row items-center gap-8">
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={data.mat_totals}
                  dataKey="volume"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={110}
                  labelLine={false}
                  label={<PieLabel />}
                >
                  {data.mat_totals.map((entry, idx) => (
                    <Cell key={entry.name} fill={getColor(entry.name, idx)} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value, name) => [`${value.toLocaleString()} m³`, name]}
                />
                <Legend
                  wrapperStyle={{ fontSize: 12 }}
                  formatter={(value) => <span style={{ color: '#475569' }}>{value}</span>}
                />
              </PieChart>
            </ResponsiveContainer>

            {/* Material breakdown table */}
            <div className="w-full md:w-64 flex-shrink-0">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Breakdown</p>
              <div className="space-y-2">
                {data.mat_totals.map((m, idx) => {
                  const pct = data.total_volume > 0
                    ? Math.round((m.volume / data.total_volume) * 100)
                    : 0;
                  const color = getColor(m.name, idx);
                  return (
                    <div key={m.name}>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="flex items-center gap-1.5">
                          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} />
                          <span className="font-medium text-slate-700">{m.name}</span>
                        </span>
                        <span className="text-slate-500">{pct}%</span>
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{ width: `${pct}%`, backgroundColor: color }}
                        />
                      </div>
                      <div className="flex justify-between text-xs text-slate-400 mt-0.5">
                        <span>{m.volume.toLocaleString()} m³</span>
                        <span>{m.trips.toLocaleString()} trips</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductionAnalyticsChart;
